import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import { EligibilityYesAndNo } from "@/blocks/EligibilityYesAndNo";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";

import { EligibilityAddHouseholdMemberScreen } from "./EligibilityAddHouseholdMemberScreen";

export const EligibilitySupportDisabledIndividualScreen = () => {
  const router = useRouter();

  const { supporterFirstName, supporterHouseholdMemberId } =
    useLocalSearchParams<{
      supporterFirstName: string;
      supporterHouseholdMemberId: string;
    }>();

  const { mutate: updateHousehold, isPending: isUpdatingHousehold } =
    useUpdateHouseholdMutation();
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { data: household } = useGetHouseholdQuery();

  const hasFinishedEligibilityFlow =
    household?.eligibilityLastStep === "FINISHED";

  const [isAddingNewHouseholdMember, setIsAddingNewHouseholdMember] =
    useState(false);

  const handleYesPress = () => {
    setIsAddingNewHouseholdMember(true);
  };

  if (!currentHouseholdMember?.householdId) {
    return <VnLoadingSpinner className="mt-2" />;
  }

  const goToNextStep = () => {
    updateHousehold(
      {
        id: currentHouseholdMember.householdId,
        householdData: {
          eligibilityLastStep: hasFinishedEligibilityFlow
            ? "FINISHED"
            : "SUPPORT_DISABLED_INDIVIDUAL",
        },
      },
      {
        onSuccess: () => {
          router.navigate({
            pathname: "/eligibility/support-other-relatives",
            params: {
              supporterFirstName,
              supporterHouseholdMemberId,
            },
          });
        },
      },
    );
  };

  if (isAddingNewHouseholdMember) {
    return (
      <EligibilityAddHouseholdMemberScreen
        relationshipOptions={[
          "PARENT",
          "GRANDPARENT",
          "SIBLING",
          "CHILD",
          "STEPCHILD",
          "GRANDCHILD",
          "FOSTER_CHILD",
          "OTHER",
        ]}
        assumptions={{
          taxFilingStatus: "NON_FILER",
          maritalStatus: "NEVER_MARRIED",
          childCaregiverStatus: "NOT_CAREGIVER",
          householdMemberRelationship: {
            livingWithStatus: true,
            taxRelationship: supporterHouseholdMemberId
              ? "DEPENDENT_OF_SUPPORTER"
              : "SUPPORT_OF",
          },
          sameMailingAddress: true,
        }}
        titleI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilitySupportDisabledIndividualScreen.supporterFormTitle"
            : "native:eligibilitySupportDisabledIndividualScreen.formTitle"
        }
        descriptionI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilitySupportDisabledIndividualScreen.supporterFormDescription"
            : "native:eligibilitySupportDisabledIndividualScreen.formDescription"
        }
        titleI18nOptions={{
          supporterFirstName,
        }}
        descriptionI18nOptions={{
          supporterFirstName,
        }}
        onSuccessOrContinue={goToNextStep}
        onBackButtonPress={() => setIsAddingNewHouseholdMember(false)}
      />
    );
  }

  return (
    <EligibilityYesAndNo
      titleI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilitySupportDisabledIndividualScreen.supporterTitle"
          : "native:eligibilitySupportDisabledIndividualScreen.title"
      }
      descriptionI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilitySupportDisabledIndividualScreen.supporterDescription"
          : "native:eligibilitySupportDisabledIndividualScreen.description"
      }
      descriptionI18nOptions={{
        supporterFirstName,
      }}
      titleI18nOptions={{
        supporterFirstName,
      }}
      onNoPress={goToNextStep}
      isNoLoading={isUpdatingHousehold}
      onYesPress={handleYesPress}
    />
  );
};
